import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, Building2, CheckCircle, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/companies`);
      setCompanies(response.data.companies || []);
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const verifyABN = async (companyId, verified) => {
    try {
      await axios.patch(`${API}/admin/companies/${companyId}/verify-abn`, { verified });
      toast.success(verified ? 'ABN verified' : 'ABN verification removed');
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to update ABN verification');
    }
  };

  const filtered = companies.filter(c => 
    !search || 
    c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.abn?.includes(search)
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Company Management</h1>
          <p className="text-muted-foreground">Manage all companies and ABN/ACN verification</p>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by company name or ABN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Companies ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">Company</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">ABN</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Industry</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Verification</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-8">Loading...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-8">No companies found</td></tr>
                  ) : (
                    filtered.map((company) => (
                      <tr key={company.id} className="border-b hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="font-medium">{company.company_name}</div>
                          <div className="text-xs text-muted-foreground">{company.contact_email}</div>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">{company.abn || 'N/A'}</td>
                        <td className="px-4 py-3">{company.industry || 'N/A'}</td>
                        <td className="px-4 py-3">
                          {company.abn_verified ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-yellow-100 text-yellow-800">Unverified</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {company.abn_verified ? (
                            <button
                              onClick={() => verifyABN(company.id, false)}
                              className="p-2 hover:bg-muted rounded text-red-600"
                              title="Remove verification"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => verifyABN(company.id, true)}
                              className="p-2 hover:bg-muted rounded text-green-600"
                              title="Verify ABN"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCompanies;
